import { Box, Button, Card, CircularProgress, Container, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Grid, IconButton, Typography } from "@material-ui/core";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import DeleteIcon from "@material-ui/icons/Delete";
import {
  getBuilderData,
  getBuilderPermissions,
  getConfig,
  getPorfoliCard,
  getUserData,
  postSectionOrder,
  duplicateSection,
  removeDuplicateSection,
  postCustomCTA,
  postVideo,
} from "adapters";
import axios, { AxiosResponse } from "axios";
import { BusinessCard } from "components/business-card";
import { AppStoreList } from "components/shared/app-store-list";
import { FileCarousel } from "components/shared/file-carousel";
import { LoaderWithOverlay } from "components/shared/loader-with-overlay";
import { PageLoader } from "components/shared/loaders/page-loader";
import { ModuleSettings } from "components/shared/module-settings";
import { PaymentOptionList } from "components/shared/payment-options-list";
import { PermissionsControl } from "components/shared/permission-control";
import { RichTextEditor } from "components/shared/rich-text-editor";
import { SectionInfoTooltip } from "components/shared/section-info-tooltip";
import { useEffect, useRef, useState } from "react";
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from "react-beautiful-dnd";
import ReactPlayer from "react-player";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { GetCardData, SetCard } from "store/card";
import { SetConfigData } from "store/config/actions";
import { GetConfigData } from "store/config/selectors";
import {
  GetUrlBuilderData,
  selectBuilderPermissions,
  SetBuilderPermissions,
  SetUrlBuilderData,
  SetUrlBuilderPublish,
} from "store/url-builder";
import { DuplicateVideo } from "store/url-builder/types";
import roundedButtonStyle from "styles/buttons/rounded-button.module.scss";
import { BuilderPermissions } from "types/permissions";
import { UserData } from "types/userData";
import { isWhite } from "utils";
import { getVideoUrl } from "utils/pathBuilder";
import { findPermissionByModule, findPermissionByName } from "utils/permissions";
import { SectionValues } from "utils/sections";
import styles from "./url-designer.module.scss";
import { AboutMe } from "./about-me";
import { VideoUpload } from "./add-video";
import { AppStores } from "./app-stores";
import { ContactMe } from "./contact-me";
import { DesignerControlBar } from "./control-bar";
import { CustomCTA } from "./custom-CTA";
import { Information } from "./information";
import { SECTION_TOOLTIPS } from "./section-tooltips";
import { SocialNetworks } from "./social-network/socials-networks";
import { Testimonials } from "./testimonials";

export interface UrlDesignerProps {
  builderType: string;
}

export default function URLDesigner({ builderType }: UrlDesignerProps) {
  const [loading, setLoading] = useState(true);
  const [isOrderUpdating, setIsOrderUpdating] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [isRemovingDuplicate, setIsRemovingDuplicate] = useState(false);
  const [userData, setUserData] = useState<UserData>({
    userPlan: "FREE",
    userId: -1,
  });
  const dispatch = useDispatch();
  const { cardId }: { cardId: string } = useParams();
  const builderData = useSelector(GetUrlBuilderData);
  const portCard = useSelector(GetCardData);
  const permissions = useSelector(selectBuilderPermissions);
  const confData = useSelector(GetConfigData);
  const [permissionSetId, setPermissionSetId] = useState(0);

  const canChangeSection = (moduleId: number) => {
    if (isFree) {
      return false;
    }

    const changeablePermission = findPermissionByName(
      findPermissionByModule(moduleId, permissions),
      "changeable"
    );

    if (builderData.urlDesignType !== "PRO") {
      return true;
    }

    return changeablePermission?.objectPermissionValue !== false;
  };
  
  // Confirmation dialog states
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [pendingDuplicateAction, setPendingDuplicateAction] = useState<{ sectionType: 'about' | 'video' | 'gallery' | 'cta', duplicateId: string } | null>(null);
  const [pendingRemoveAction, setPendingRemoveAction] = useState<{ duplicateType: string; sectionId: string } | null>(null);

  const [sections, setSections] = useState(SectionValues);
  const [prevSections, setPrevSections] = useState(SectionValues);

  const [isDragging, setIsDragging] = useState(false);
  const [draggingId, setDraggingId] = useState<any>(null);
  const didNormalizeOrderOnFirstDrag = useRef(false);

  const [count, setCount] = useState(0);

  useEffect(() => {
    setCount((prev) => prev + 1);
  }, [draggingId]);

  useEffect(() => {
    axios
      .all(
        [
          getBuilderData(cardId, builderType),
          getConfig(),
          getBuilderPermissions(cardId, builderType, "false"),
        ].concat(
          builderType === "PRO"
            ? [getPorfoliCard(cardId), getUserData(cardId)]
            : [
              Promise.resolve({ data: {} } as AxiosResponse),
              Promise.resolve({
                data: {
                  success: true,
                  message: "UserData fetched",
                  data: {
                    userPlan: "FREE",
                    userId: -1,
                  },
                },
              } as AxiosResponse),
            ]
        )
      )
      .then(
        axios.spread(
          (
            { data: builderData },
            { data: configData },
            { data: permissionsData },
            { data: card },
            { data: userData }
          ) => {
            dispatch(SetConfigData(configData));
            builderType === "PRO" && dispatch(SetCard(card));
            dispatch(
              SetBuilderPermissions(permissionsData.data.objectPermissions)
            );
            setPermissionSetId(permissionsData.data.objectPermissionSetId);
            dispatch(SetUrlBuilderData(builderData.data));
            setUserData(userData.data);
            setLoading(false);
          }
        )
      );
  }, [dispatch, cardId, builderType]);

  //update section order
  const updateSectionOrder = (items: typeof SectionValues) => {
    setIsOrderUpdating(true);
    postSectionOrder(cardId, builderType, items)
      .then(({ data }) => {
        dispatch(SetUrlBuilderPublish(false));
      })
      .catch((err: any) => {
        setSections(prevSections);
      })
      .finally(() => {
        setIsOrderUpdating(false);
      });
  };

  //reorder
  const reorder = <T,>(
    list: T[],
    startIndex: number,
    endIndex: number
  ): T[] => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  const getDuplicateType = (
    sectionKey: string,
    data = builderData
  ) => {
    if (data.duplicateAbout?.some(item => item._duplicateId === sectionKey)) return "about";
    if (data.duplicateVideos?.some(item => item._duplicateId === sectionKey)) return "video";
    if (data.duplicateGalleries?.some(item => item._duplicateId === sectionKey)) return "gallery";
    if (data.duplicateCustomCTAs?.some(item => item._duplicateId === sectionKey)) return "cta";
    return null;
  };

  const getOrderHasBaseSections = (order: string[]) =>
    order.some(sectionKey => SectionValues.includes(sectionKey as any));

  const hasAnyDuplicates = (data = builderData) =>
    (data.duplicateAbout?.length ?? 0) > 0 ||
    (data.duplicateVideos?.length ?? 0) > 0 ||
    (data.duplicateGalleries?.length ?? 0) > 0 ||
    (data.duplicateCustomCTAs?.length ?? 0) > 0;

  const buildDefaultOrderWithDuplicates = (
    order: string[],
    data = builderData
  ) => {
    const duplicatesByType = SectionValues.reduce((acc, sectionType) => {
      acc[sectionType] = [];
      return acc;
    }, {} as Record<string, string[]>);

    if (order.length > 0) {
      order.forEach((sectionKey) => {
        const duplicateType = getDuplicateType(sectionKey, data);
        if (duplicateType && duplicatesByType[duplicateType]) {
          duplicatesByType[duplicateType].push(sectionKey);
        }
      });
    } else {
      (data.duplicateAbout || []).forEach(item => duplicatesByType.about.push(item._duplicateId));
      (data.duplicateVideos || []).forEach(item => duplicatesByType.video.push(item._duplicateId));
      (data.duplicateGalleries || []).forEach(item => duplicatesByType.gallery.push(item._duplicateId));
      (data.duplicateCustomCTAs || []).forEach(item => duplicatesByType.cta.push(item._duplicateId));
    }

    const normalizedOrder: string[] = [];
    SectionValues.forEach((sectionType) => {
      normalizedOrder.push(sectionType);
      const duplicatesForType = duplicatesByType[sectionType];
      if (duplicatesForType?.length) {
        normalizedOrder.push(...duplicatesForType);
      }
    });

    return normalizedOrder;
  };

  const normalizeOrderIfNeeded = (data = builderData) => {
    const order = data.order || [];
    if (!hasAnyDuplicates(data)) return order;
    if (order.length > 0 && getOrderHasBaseSections(order)) return order;
    return buildDefaultOrderWithDuplicates(order, data);
  };

  const areArraysEqual = (a: string[], b: string[]) =>
    a.length === b.length && a.every((item, index) => item === b[index]);

  const buildSectionsToRender = () => {
    const order = builderData.order || [];
    const allSectionsToRender: Array<{
      key: string;
      isIndexed: boolean;
      duplicateId?: string;
    }> = [];

    const orderToRender = normalizeOrderIfNeeded({ ...builderData, order });

    // Parse order array to get all sections
    orderToRender.forEach((sectionKey) => {
      const duplicateType = getDuplicateType(sectionKey);

      if (duplicateType) {
        allSectionsToRender.push({
          key: sectionKey,
          isIndexed: true,
          duplicateId: sectionKey,
        });
      } else if (SectionValues.includes(sectionKey as any)) {
        allSectionsToRender.push({ key: sectionKey, isIndexed: false });
      }
    });

    // Also add base sections that might not be in order
    sections.forEach((section) => {
      if (!allSectionsToRender.find(s => s.key === section && !s.isIndexed)) {
        allSectionsToRender.push({ key: section, isIndexed: false });
      }
    });

    return allSectionsToRender;
  };

  const onDragEnd = (result: DropResult) => {
    setIsDragging(false);
    setDraggingId(null);

    // dropped outside the list
    if (!result.destination) return;
    
    const builderOrder = builderData.order || [];
    const normalizedOrderForDrag = normalizeOrderIfNeeded({
      ...builderData,
      order: builderOrder,
    });
    const shouldNormalizeOnDrag =
      !didNormalizeOrderOnFirstDrag.current &&
      !areArraysEqual(normalizedOrderForDrag, builderOrder);

    if (shouldNormalizeOnDrag) {
      didNormalizeOrderOnFirstDrag.current = true;
    }

    // Get the current order array which includes both base sections and duplicate sections
    const currentOrder = shouldNormalizeOnDrag
      ? normalizedOrderForDrag
      : buildSectionsToRender().map((sectionItem) => sectionItem.key);
    
    // Reorder the full order array
    const reorderedOrder = reorder(
      currentOrder,
      result.source.index,
      result.destination.index
    );
    
    // Check if order changed
    const isSame =
      currentOrder.length === reorderedOrder.length &&
      currentOrder.every((item, index) => item === reorderedOrder[index]);
    
    if (!isSame) {
      // Optimistically update the Redux state immediately so the UI updates
      dispatch(SetUrlBuilderData({
        ...builderData,
        order: reorderedOrder
      }));
      
      // Update the order array via API
      setIsOrderUpdating(true);
      postSectionOrder(cardId, builderType, reorderedOrder)
        .then(({ data }) => {
          if (data.success) {
            // Refetch to ensure we have the latest state from server
            getBuilderData(cardId, builderType)
              .then(({ data: refetchedData }) => {
                if (refetchedData.success) {
                  dispatch(SetUrlBuilderData(refetchedData.data));
                }
              })
              .catch((err) => {
                console.error('Error refetching builder data:', err);
              });
          }
          dispatch(SetUrlBuilderPublish(false));
        })
        .catch((err: any) => {
          console.error('Error updating section order:', err);
          // Revert to original order on error
          dispatch(SetUrlBuilderData({
            ...builderData,
            order: currentOrder
          }));
        })
        .finally(() => {
          setIsOrderUpdating(false);
        });
    }
  };

  const onDragStart = (result: DropResult) => {
    setDraggingId(result.draggableId);
    setIsDragging(true);
  };

  useEffect(() => {
    const order = builderData.order || [];
    // Sort base sections according to order (for drag-drop functionality)
    const sortedBaseSections = sections
      .slice(0)
      .sort((a, b) => {
        const aIndex = order.indexOf(a);
        const bIndex = order.indexOf(b);
        // If section not in order, put it at the end
        if (aIndex === -1 && bIndex === -1) return 0;
        if (aIndex === -1) return 1;
        if (bIndex === -1) return -1;
        return aIndex - bIndex;
      });

    setSections(sortedBaseSections);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [builderData.order]);

  useEffect(() => {
    let lastPoint = { x: 0, y: 0 };
    let isScrolled = false;

    const scrollListner = () => {
      isScrolled = true;
    };

    const mousemoveListner = (e: MouseEvent) => {
      window.addEventListener("scroll", scrollListner);
      if (draggingId) {
        const x = document.getElementById(draggingId);
        if (x?.clientHeight && x?.clientHeight >= 400) {
          if (!isScrolled) {
            if (e.clientY > lastPoint.y) {
              window.scrollBy({
                left: 0,
                top: 200,
                behavior: "smooth",
              });
            } else if (e.clientY < lastPoint.y) {
              window.scrollBy({
                left: 0,
                top: -200,
                behavior: "smooth",
              });
            }
          }
        }
      }
      lastPoint.x = e.clientX;
      lastPoint.y = e.clientY;
    };

    window.addEventListener("mousemove", mousemoveListner);
    return () => {
      window.removeEventListener("mousemove", mousemoveListner);
      window.removeEventListener("scroll", scrollListner);
    };
  }, [draggingId, isDragging]);

  if (loading) return <PageLoader />;

  const isFree = userData.userPlan == "FREE" && builderData.urlDesignType !== "ENTERPRISE_TEMPLATE";
  const DUPLICATE_LIMIT = 5;

  const getDuplicateCount = (sectionType: 'about' | 'video' | 'gallery' | 'cta') => {
    switch (sectionType) {
      case 'about':
        return builderData.duplicateAbout?.length ?? 0;
      case 'video':
        return builderData.duplicateVideos?.length ?? 0;
      case 'gallery':
        return builderData.duplicateGalleries?.length ?? 0;
      case 'cta':
        return builderData.duplicateCustomCTAs?.length ?? 0;
      default:
        return 0;
    }
  };

  // Handle duplicate section - show confirmation dialog first
  const handleDuplicateSection = (sectionType: 'about' | 'video' | 'gallery' | 'cta', duplicateId: string) => {
    if (getDuplicateCount(sectionType) >= DUPLICATE_LIMIT) {
      toast.error("You can only duplicate this section up to 5 times.");
      return;
    }
    setPendingDuplicateAction({ sectionType, duplicateId });
    setDuplicateDialogOpen(true);
  };

  // Execute duplicate after confirmation
  const confirmDuplicateSection = async () => {
    if (!pendingDuplicateAction) return;
    
    const { sectionType, duplicateId } = pendingDuplicateAction;
    if (getDuplicateCount(sectionType) >= DUPLICATE_LIMIT) {
      toast.error("You can only duplicate this section up to 5 times.");
      setDuplicateDialogOpen(false);
      setPendingDuplicateAction(null);
      return;
    }
    // Map section type to API duplicateType
    const duplicateTypeMap: Record<'about' | 'video' | 'gallery' | 'cta', 'About' | 'CustomCTA' | 'Video' | 'Gallery'> = {
      'about': 'About',
      'video': 'Video',
      'gallery': 'Gallery',
      'cta': 'CustomCTA',
    };

    const duplicateType = duplicateTypeMap[sectionType];
    
    const previousDuplicateIds =
      sectionType === "video"
        ? (builderData.duplicateVideos || []).map((video) => video._duplicateId)
        : [];

    try {
      setIsDuplicating(true);
      const { data } = await duplicateSection(cardId, builderType, duplicateType);
      if (data.success) {
        // Refetch builder data to get the latest state
        const { data: refetchedData } = await getBuilderData(cardId, builderType);
        if (refetchedData.success) {
          const normalizedOrder = normalizeOrderIfNeeded(refetchedData.data);
          const shouldPersistNormalizedOrder = !areArraysEqual(
            normalizedOrder,
            refetchedData.data.order || []
          );
          const normalizedData = shouldPersistNormalizedOrder
            ? { ...refetchedData.data, order: normalizedOrder }
            : refetchedData.data;

          if (shouldPersistNormalizedOrder) {
            didNormalizeOrderOnFirstDrag.current = true;
            dispatch(SetUrlBuilderData(normalizedData));
            postSectionOrder(cardId, builderType, normalizedOrder).catch((err: any) => {
              console.error("Error normalizing section order after duplicate:", err);
            });
          } else {
            dispatch(SetUrlBuilderData(refetchedData.data));
          }

          if (sectionType === "video") {
            const newDuplicateVideos = (refetchedData.data.duplicateVideos || []) as DuplicateVideo[];
            const newlyCreatedVideos = newDuplicateVideos.filter(
              (video) => !previousDuplicateIds.includes(video._duplicateId)
            );

            for (const duplicateVideo of newlyCreatedVideos) {
              const clearPayload: any = {
                actionType: null,
                file: null,
                videoUrl: "",
                _duplicateId: duplicateVideo._duplicateId,
              };
              const { data: clearedData } = await postVideo(
                cardId,
                builderType,
                clearPayload,
                portCard?.templateId?.toString() || ""
              );

              if (clearedData.success) {
                const updatedOrder = shouldPersistNormalizedOrder
                  ? normalizedOrder
                  : clearedData.data.order;
                dispatch(SetUrlBuilderData({
                  ...clearedData.data,
                  order: updatedOrder,
                }));
              }
            }
          }
        }
        dispatch(SetUrlBuilderPublish(false));
      }
    } catch (error) {
      console.error('Error duplicating section:', error);
    } finally {
      setDuplicateDialogOpen(false);
      setPendingDuplicateAction(null);
      setIsDuplicating(false);
    }
  };

  // Handle remove duplicate section - show confirmation dialog first
  const handleRemoveDuplicateSection = (duplicateType: string, sectionId: string) => {
    setPendingRemoveAction({ duplicateType, sectionId });
    setRemoveDialogOpen(true);
  };

  // Execute remove after confirmation
  const confirmRemoveDuplicateSection = async () => {
    if (!pendingRemoveAction) return;

    try {
      setIsRemovingDuplicate(true);
      const { data } = await removeDuplicateSection(cardId, builderType, pendingRemoveAction.duplicateType, pendingRemoveAction.sectionId);
      if (data.success) {
        // Refetch builder data to get the latest state
        const { data: refetchedData } = await getBuilderData(cardId, builderType);
        if (refetchedData.success) {
          const normalizedOrder = normalizeOrderIfNeeded(refetchedData.data);
          const shouldPersistNormalizedOrder = !areArraysEqual(
            normalizedOrder,
            refetchedData.data.order || []
          );
          const normalizedData = shouldPersistNormalizedOrder
            ? { ...refetchedData.data, order: normalizedOrder }
            : refetchedData.data;

          if (shouldPersistNormalizedOrder) {
            didNormalizeOrderOnFirstDrag.current = true;
            dispatch(SetUrlBuilderData(normalizedData));
            postSectionOrder(cardId, builderType, normalizedOrder).catch((err: any) => {
              console.error("Error normalizing section order after remove:", err);
            });
          } else {
            dispatch(SetUrlBuilderData(refetchedData.data));
          }
        }
        dispatch(SetUrlBuilderPublish(false));
      }
    } catch (error) {
      console.error('Error removing duplicate section:', error);
    } finally {
      setRemoveDialogOpen(false);
      setPendingRemoveAction(null);
      setIsRemovingDuplicate(false);
    }
  };

  // Count CTA buttons in a specific section
  const countCTAButtons = (duplicateId?: string) => {
    // Get the CTA data for this specific section
    const ctaData = duplicateId 
      ? builderData.duplicateCustomCTAs?.find(cta => cta._duplicateId === duplicateId)
      : builderData.customCTA;
    
    if (!ctaData || !ctaData.buttonText) {
      return 0;
    }
    
    // Parse the delimited strings to count buttons
    const buttonTexts = ctaData.buttonText.split("#lcard;").filter(text => text.trim());
    return buttonTexts.length;
  };

  // Shuffle CTA buttons within a specific section
  const handleShuffleCTAButtons = async (duplicateId?: string) => {
    // Get the CTA data for this specific section
    const ctaData = duplicateId 
      ? builderData.duplicateCustomCTAs?.find(cta => cta._duplicateId === duplicateId)
      : builderData.customCTA;
    
    if (!ctaData || !ctaData.buttonText) {
      return;
    }
    
    // Parse the delimited strings into arrays
    const buttonTexts = ctaData.buttonText
      ? ctaData.buttonText.split("#lcard;").filter(text => text.trim())
      : [];
    const buttonLinks = ctaData.buttonLink
      ? ctaData.buttonLink.split("#lcard;").filter(link => link.trim())
      : [];
    const buttonColors = ctaData.buttonColor
      ? ctaData.buttonColor.split("#lcard;").filter(color => color.trim())
      : [];
    
    // Create array of button objects
    const buttons: Array<{ buttonText: string; buttonLink: string; buttonColor: string }> = [];
    const maxLength = Math.max(buttonTexts.length, buttonLinks.length);
    
    for (let i = 0; i < maxLength; i++) {
      if (buttonTexts[i] && buttonLinks[i]) {
        buttons.push({
          buttonText: buttonTexts[i],
          buttonLink: buttonLinks[i],
          buttonColor: buttonColors[i] || builderData.primaryColor.color,
        });
      }
    }
    
    // Only shuffle if there are 2+ buttons
    if (buttons.length < 2) {
      return;
    }
    
    // Fisher-Yates shuffle algorithm
    const shuffledButtons = [...buttons];
    for (let i = shuffledButtons.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffledButtons[i], shuffledButtons[j]] = [shuffledButtons[j], shuffledButtons[i]];
    }
    
    // Reconstruct the delimited strings
    const shuffledCTA: { buttonText: string; buttonLink: string; buttonColor: string; _duplicateId?: string } = {
      buttonText: "",
      buttonLink: "",
      buttonColor: "",
    };
    
    shuffledButtons.forEach((button) => {
      shuffledCTA.buttonText = shuffledCTA.buttonText
        ? shuffledCTA.buttonText + button.buttonText + "#lcard;"
        : button.buttonText + "#lcard;";
      shuffledCTA.buttonLink = shuffledCTA.buttonLink
        ? shuffledCTA.buttonLink + button.buttonLink + "#lcard;"
        : button.buttonLink + "#lcard;";
      shuffledCTA.buttonColor = shuffledCTA.buttonColor
        ? shuffledCTA.buttonColor + button.buttonColor + "#lcard;"
        : button.buttonColor + "#lcard;";
    });
    
    // Include duplicateId if it's a duplicate section
    if (duplicateId) {
      shuffledCTA._duplicateId = duplicateId;
    }
    
    // Update via API
    setIsOrderUpdating(true);
    postCustomCTA(cardId, builderType, shuffledCTA)
      .then(({ data }) => {
        if (data.success) {
          dispatch(SetUrlBuilderData(data.data));
          dispatch(SetUrlBuilderPublish(false));
        }
      })
      .catch((err: any) => {
        console.error('Error shuffling CTA buttons:', err);
      })
      .finally(() => {
        setIsOrderUpdating(false);
      });
  };

  return (
    <>
      {isOrderUpdating && <LoaderWithOverlay />}
      <Container maxWidth="sm" className="root-wrapper">
        <Grid container justifyContent="center">
          <Grid item container xs={12} spacing={0}>
            {/* <Grid item xs={12}>
       <LcardBadge />
      </Grid> */}
            <Grid item xs={12}>
              <BusinessCard
                cardId={cardId}
                builderType={builderType}
                cardImage={portCard.cardImage}
              />
            </Grid>
            <Grid item xs={12} className="mt-4">
              <Card style={{ overflow: "visible" }}>
                <Grid container style={{ padding: "2em" }} spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom className={styles.sectionTitle}>
                      Information  {isFree && <span className="pro_tag c-pointer">PRO</span>}
                      <SectionInfoTooltip content={SECTION_TOOLTIPS.information} />
                      <ModuleSettings
                        permissions={findPermissionByModule(2, permissions)}
                        builderType={builderData.urlDesignType}
                      />
                    </Typography>
                    <PermissionsControl
                      permissions={findPermissionByModule(2, permissions)}
                      builderType={builderData.urlDesignType}
                    >
                      <Information builderType={builderType} isFree={isFree} />
                    </PermissionsControl>
                  </Grid>

                  <DragDropContext
                    onDragEnd={onDragEnd}
                    onDragStart={onDragStart}
                  >
                    <Droppable droppableId="droppable">
                      {(provided, snapshot) => (
                        <div
                          {...provided.droppableProps}
                          ref={provided.innerRef}
                          className="dnd-wrapper"
                          id="dnd-wrapper"
                        >
                          {(() => {
                            const allSectionsToRender = buildSectionsToRender();

                            return allSectionsToRender.map((sectionItem, index) => {
                              const section = sectionItem.key;
                              const isIndexed = sectionItem.isIndexed;
                              const duplicateId = sectionItem.duplicateId;

                              // For indexed sections, render with drag-drop
                              if (isIndexed && duplicateId) {
                                const duplicateAbout = builderData.duplicateAbout?.find(item => item._duplicateId === duplicateId);
                                const duplicateVideo = builderData.duplicateVideos?.find(item => item._duplicateId === duplicateId);
                                const duplicateGallery = builderData.duplicateGalleries?.find(item => item._duplicateId === duplicateId);
                                const duplicateCTA = builderData.duplicateCustomCTAs?.find(item => item._duplicateId === duplicateId);

                                return (
                                  <Draggable
                                    key={section}
                                    draggableId={duplicateId}
                                    index={index}
                                  >
                                    {(provided, snapshot) => (
                                      <div
                                        ref={provided.innerRef}
                                        {...provided.dragHandleProps}
                                        {...provided.draggableProps}
                                        key={section}
                                      >
                                    {duplicateAbout && (
                                      <Grid
                                        item
                                        xs={12}
                                        className="section-wrapper"
                                        id={section}
                                        style={{ borderLeft: '3px solid #2196f3', paddingLeft: '1em' }}
                                      >
                                        <Box position="relative">
                                          <Typography variant="h6" gutterBottom className={styles.sectionTitle}>
                                            About 
                                            <span className={styles.duplicateBadge}>
                                              Duplicate
                                            </span>
                                            {isFree && <span className="pro_tag c-pointer">PRO</span>}
                                            <SectionInfoTooltip content={SECTION_TOOLTIPS.about} />
                                          </Typography>
                                          {canChangeSection(9) && (
                                            <Box style={{ position: 'absolute', top: 0, right: 0, display: 'flex', gap: '4px' }}>
                                              <Button
                                                size="small"
                                                onClick={() => handleDuplicateSection('about', duplicateAbout._duplicateId)}
                                                variant="outlined"
                                                startIcon={<FileCopyIcon style={{ fontSize: '14px' }} />}
                                                className={styles.iconOnlyMobileButton}
                                                style={{ 
                                                  textTransform: 'none', 
                                                  fontSize: '0.65rem',
                                                  padding: '2px 8px',
                                                  minWidth: 'auto'
                                                }}
                                              >
                                                <span className={styles.iconOnlyMobileLabel}>Duplicate</span>
                                              </Button>
                                              <Button
                                                size="small"
                                                onClick={() => handleRemoveDuplicateSection('About', duplicateAbout._duplicateId)}
                                                variant="outlined"
                                                color="secondary"
                                                startIcon={<DeleteIcon style={{ fontSize: '14px' }} />}
                                                className={styles.iconOnlyMobileButton}
                                                style={{ 
                                                  textTransform: 'none', 
                                                  fontSize: '0.65rem',
                                                  padding: '2px 8px',
                                                  minWidth: 'auto',
                                                  color: '#f44336', 
                                                  borderColor: '#f44336' 
                                                }}
                                              >
                                                <span className={styles.iconOnlyMobileLabel}>Remove</span>
                                              </Button>
                                            </Box>
                                          )}
                                        </Box>
                                        <PermissionsControl
                                          permissions={findPermissionByModule(9, permissions)}
                                          builderType={builderData.urlDesignType}
                                        >
                                          <AboutMe 
                                            builderType={builderType} 
                                            isFree={isFree}
                                            duplicateId={duplicateAbout._duplicateId}
                                            initialText={duplicateAbout.text}
                                          />
                                        </PermissionsControl>
                                      </Grid>
                                    )}
                                    {duplicateVideo && (
                                      <Grid
                                        item
                                        xs={12}
                                        className="section-wrapper"
                                        id={section}
                                        style={{ borderLeft: '3px solid #2196f3', paddingLeft: '1em' }}
                                      >
                                        <Box position="relative">
                                          <Typography variant="h6" gutterBottom className={styles.sectionTitle}>
                                            Video 
                                            <span className={styles.duplicateBadge}>
                                              Duplicate
                                            </span>
                                            {isFree && <span className="pro_tag c-pointer">PRO</span>}
                                            <SectionInfoTooltip content={SECTION_TOOLTIPS.video} />
                                          </Typography>
                                            {canChangeSection(11) && (
                                              <Box style={{ position: 'absolute', top: 0, right: 0, display: 'flex', gap: '4px' }}>
                                                <Button
                                                  size="small"
                                                  onClick={() => handleDuplicateSection('video', duplicateVideo._duplicateId)}
                                                  variant="outlined"
                                                  startIcon={<FileCopyIcon style={{ fontSize: '14px' }} />}
                                                  className={styles.iconOnlyMobileButton}
                                                  style={{ 
                                                    textTransform: 'none', 
                                                    fontSize: '0.65rem',
                                                    padding: '2px 8px',
                                                    minWidth: 'auto'
                                                  }}
                                                >
                                                  <span className={styles.iconOnlyMobileLabel}>Duplicate</span>
                                                </Button>
                                                <Button
                                                  size="small"
                                                  onClick={() => handleRemoveDuplicateSection('Video', duplicateVideo._duplicateId)}
                                                  variant="outlined"
                                                  color="secondary"
                                                  startIcon={<DeleteIcon style={{ fontSize: '14px' }} />}
                                                  className={styles.iconOnlyMobileButton}
                                                  style={{ 
                                                    textTransform: 'none', 
                                                    fontSize: '0.65rem',
                                                    padding: '2px 8px',
                                                    minWidth: 'auto',
                                                    color: '#f44336', 
                                                    borderColor: '#f44336' 
                                                  }}
                                                >
                                                  <span className={styles.iconOnlyMobileLabel}>Remove</span>
                                                </Button>
                                              </Box>
                                            )}
                                        </Box>
                                        <PermissionsControl
                                          permissions={findPermissionByModule(11, permissions)}
                                          builderType={builderData.urlDesignType}
                                          readonlyForChangablePro
                                          inlineNotChangeableNotice
                                        >
                                          <VideoUpload 
                                            builderType={builderType}
                                            isFree={isFree}
                                            duplicateId={duplicateVideo._duplicateId}
                                            initialVideo={duplicateVideo}
                                          />
                                        </PermissionsControl>
                                      </Grid>
                                    )}
                                    {duplicateGallery && (
                                      <Grid
                                        item
                                        xs={12}
                                        className="section-wrapper"
                                        id={section}
                                        style={{ borderLeft: '3px solid #2196f3', paddingLeft: '1em' }}
                                      >
                                          <Box position="relative">
                                            <Typography variant="h6" gutterBottom className={styles.sectionTitle}>
                                              Gallery 
                                              <span className={styles.duplicateBadge}>
                                                Duplicate
                                              </span>
                                              {isFree && <span className="pro_tag c-pointer">PRO</span>}
                                              <SectionInfoTooltip content={SECTION_TOOLTIPS.gallery} />
                                            </Typography>
                                            {canChangeSection(14) && (
                                              <Box style={{ position: 'absolute', top: 0, right: 0, display: 'flex', gap: '4px' }}>
                                                <Button
                                                  size="small"
                                                  onClick={() => handleDuplicateSection('gallery', duplicateGallery._duplicateId)}
                                                  variant="outlined"
                                                  startIcon={<FileCopyIcon style={{ fontSize: '14px' }} />}
                                                  className={styles.iconOnlyMobileButton}
                                                  style={{ 
                                                    textTransform: 'none', 
                                                    fontSize: '0.65rem',
                                                    padding: '2px 8px',
                                                    minWidth: 'auto'
                                                  }}
                                                >
                                                  <span className={styles.iconOnlyMobileLabel}>Duplicate</span>
                                                </Button>
                                                <Button
                                                  size="small"
                                                  onClick={() => handleRemoveDuplicateSection('Gallery', duplicateGallery._duplicateId)}
                                                  variant="outlined"
                                                  color="secondary"
                                                  startIcon={<DeleteIcon style={{ fontSize: '14px' }} />}
                                                  className={styles.iconOnlyMobileButton}
                                                  style={{ 
                                                    textTransform: 'none', 
                                                    fontSize: '0.65rem',
                                                    padding: '2px 8px',
                                                    minWidth: 'auto',
                                                    color: '#f44336', 
                                                    borderColor: '#f44336' 
                                                  }}
                                                >
                                                  <span className={styles.iconOnlyMobileLabel}>Remove</span>
                                                </Button>
                                              </Box>
                                            )}
                                        </Box>
                                        <PermissionsControl
                                          permissions={findPermissionByModule(14, permissions)}
                                          builderType={builderData.urlDesignType}
                                          readonlyForChangablePro
                                          inlineNotChangeableNotice
                                        >
                                          <FileCarousel
                                            files={duplicateGallery._Images || []}
                                            themeColor={builderData.primaryColor.color}
                                            enablePreview
                                            builderType={builderType}
                                            slidesPerRow={2}
                                            isFree={isFree}
                                            duplicateId={duplicateGallery._duplicateId}
                                          />
                                        </PermissionsControl>
                                      </Grid>
                                    )}
                                    {duplicateCTA && (
                                      <Grid
                                        item
                                        xs={12}
                                        className="section-wrapper"
                                        id={section}
                                        style={{ borderLeft: '3px solid #2196f3', paddingLeft: '1em' }}
                                      >
                                          <Box position="relative">
                                            <Typography
                                              variant="h6"
                                              gutterBottom
                                              style={{ marginTop: ".5em" }}
                                              className={styles.sectionTitle}
                                            >
                                              Custom CTA Button 
                                              <span className={styles.duplicateBadge}>
                                                Duplicate
                                              </span>
                                              {isFree && <span className="pro_tag c-pointer">PRO</span>}
                                              <SectionInfoTooltip content={SECTION_TOOLTIPS.cta} />
                                            </Typography>
                                              {canChangeSection(23) && (
                                                <Box style={{ position: 'absolute', top: 0, right: 0, display: 'flex', gap: '4px' }}>
                                                  <Button
                                                    size="small"
                                                    onClick={() => handleDuplicateSection('cta', duplicateCTA._duplicateId)}
                                                    variant="outlined"
                                                    startIcon={<FileCopyIcon style={{ fontSize: '14px' }} />}
                                                    className={styles.iconOnlyMobileButton}
                                                    style={{ 
                                                      textTransform: 'none', 
                                                      fontSize: '0.65rem',
                                                      padding: '2px 8px',
                                                      minWidth: 'auto'
                                                    }}
                                                  >
                                                    <span className={styles.iconOnlyMobileLabel}>Duplicate</span>
                                                  </Button>
                                                  <Button
                                                    size="small"
                                                    onClick={() => handleRemoveDuplicateSection('CustomCTA', duplicateCTA._duplicateId)}
                                                    variant="outlined"
                                                    color="secondary"
                                                    startIcon={<DeleteIcon style={{ fontSize: '14px' }} />}
                                                    className={styles.iconOnlyMobileButton}
                                                    style={{ 
                                                      textTransform: 'none', 
                                                      fontSize: '0.65rem',
                                                      padding: '2px 8px',
                                                      minWidth: 'auto',
                                                      color: '#f44336', 
                                                      borderColor: '#f44336' 
                                                    }}
                                                  >
                                                    <span className={styles.iconOnlyMobileLabel}>Remove</span>
                                                  </Button>
                                                </Box>
                                              )}
                                          </Box>
                                        <PermissionsControl
                                          permissions={findPermissionByModule(23, permissions)}
                                          builderType={builderData.urlDesignType}
                                        >
                                          <CustomCTA 
                                            builderType={builderType}
                                            isFree={isFree}
                                            duplicateId={duplicateCTA._duplicateId}
                                            initialCTA={duplicateCTA}
                                          />
                                        </PermissionsControl>
                                      </Grid>
                                    )}
                                      </div>
                                    )}
                                  </Draggable>
                                );
                              }

                              // For base sections, use drag-drop
                              return (
                                <Draggable
                                  key={section}
                                  draggableId={section}
                                  index={index}
                                >
                                  {(provided, snapshot) => (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.dragHandleProps}
                                      {...provided.draggableProps}
                                      key={index}
                                    >
                                      {" "}
                                      {section === "about" && (
                                        <Grid
                                          item
                                          xs={12}
                                          className="section-wrapper"
                                          id="about"
                                        >
                                          <Box position="relative">
                                            <Typography variant="h6" gutterBottom className={styles.sectionTitle}>
                                              About {isFree && <span className="pro_tag c-pointer">PRO</span>}
                                              <SectionInfoTooltip content={SECTION_TOOLTIPS.about} />
                                              <ModuleSettings
                                                permissions={findPermissionByModule(
                                                  9,
                                                  permissions
                                                )}
                                                builderType={
                                                  builderData.urlDesignType
                                                }
                                              />
                                            </Typography>
                                            {canChangeSection(9) && (
                                              <Button
                                                size="small"
                                                onClick={() => handleDuplicateSection('about', '')}
                                                variant="outlined"
                                                startIcon={<FileCopyIcon style={{ fontSize: '14px' }} />}
                                                className={styles.iconOnlyMobileButton}
                                                style={{
                                                  position: 'absolute',
                                                  top: 0,
                                                  right: 0,
                                                  textTransform: 'none',
                                                  fontSize: '0.65rem',
                                                  padding: '2px 8px',
                                                  minWidth: 'auto'
                                                }}
                                              >
                                                <span className={styles.iconOnlyMobileLabel}>Duplicate</span>
                                              </Button>
                                            )}
                                          </Box>
                                          <PermissionsControl
                                            permissions={findPermissionByModule(
                                              9,
                                              permissions
                                            )}
                                            builderType={builderData.urlDesignType}
                                          >
                                            <AboutMe builderType={builderType} isFree={isFree} />
                                          </PermissionsControl>
                                        </Grid>
                                      )}
                                      {section === "social" && (
                                        <Grid
                                          item
                                          xs={12}
                                          className="section-wrapper"
                                          id="social"
                                        >
                                          <Typography variant="h6" gutterBottom className={styles.sectionTitle}>
                                            Social Networks
                                            <SectionInfoTooltip content={SECTION_TOOLTIPS.social} />
                                            <ModuleSettings
                                              permissions={findPermissionByModule(
                                                10,
                                                permissions
                                              )}
                                              builderType={
                                                builderData.urlDesignType
                                              }
                                            />
                                          </Typography>
                                          <PermissionsControl
                                            permissions={findPermissionByModule(
                                              10,
                                              permissions
                                            )}
                                            builderType={builderData.urlDesignType}
                                          >
                                            <SocialNetworks
                                              builderType={builderType}
                                            />
                                          </PermissionsControl>
                                        </Grid>
                                      )}
                                      {section === "video" && (
                                        <Grid
                                          item
                                          xs={12}
                                          className="section-wrapper"
                                          id="video"
                                        >
                                          <Box position="relative">
                                            <Typography variant="h6" gutterBottom className={styles.sectionTitle}>
                                              Video {isFree && <span className="pro_tag c-pointer">PRO</span>}
                                              <SectionInfoTooltip content={SECTION_TOOLTIPS.video} />
                                              <ModuleSettings
                                                permissions={findPermissionByModule(
                                                  11,
                                                  permissions
                                                )}
                                                builderType={
                                                  builderData.urlDesignType
                                                }
                                              />
                                            </Typography>
                                            {canChangeSection(11) && (
                                              <Button
                                                size="small"
                                                onClick={() => handleDuplicateSection('video', '')}
                                                variant="outlined"
                                                startIcon={<FileCopyIcon style={{ fontSize: '14px' }} />}
                                                className={styles.iconOnlyMobileButton}
                                                style={{
                                                  position: 'absolute',
                                                  top: 0,
                                                  right: 0,
                                                  textTransform: 'none',
                                                  fontSize: '0.65rem',
                                                  padding: '2px 8px',
                                                  minWidth: 'auto'
                                                }}
                                              >
                                                <span className={styles.iconOnlyMobileLabel}>Duplicate</span>
                                              </Button>
                                            )}
                                          </Box>
                                          <PermissionsControl
                                            permissions={findPermissionByModule(
                                              11,
                                              permissions
                                            )}
                                            builderType={builderData.urlDesignType}
                                            readonlyForChangablePro
                                            inlineNotChangeableNotice
                                          >
                                            <VideoUpload
                                              builderType={builderType}
                                              isFree={isFree}
                                            />
                                          </PermissionsControl>
                                        </Grid>
                                      )}
                                      {section === "gallery" && (
                                        <Grid
                                          item
                                          xs={12}
                                          className="section-wrapper"
                                          id="gallery"
                                        >
                                          <Box position="relative">
                                            <Typography variant="h6" gutterBottom className={styles.sectionTitle}>
                                              Gallery {isFree && <span className="pro_tag c-pointer">PRO</span>}
                                              <SectionInfoTooltip content={SECTION_TOOLTIPS.gallery} />
                                              <ModuleSettings
                                                permissions={findPermissionByModule(
                                                  14,
                                                  permissions
                                                )}
                                                builderType={
                                                  builderData.urlDesignType
                                                }
                                              />
                                            </Typography>
                                            {canChangeSection(14) && (
                                              <Button
                                                size="small"
                                                onClick={() => handleDuplicateSection('gallery', '')}
                                                variant="outlined"
                                                startIcon={<FileCopyIcon style={{ fontSize: '14px' }} />}
                                                className={styles.iconOnlyMobileButton}
                                                style={{
                                                  position: 'absolute',
                                                  top: 0,
                                                  right: 0,
                                                  textTransform: 'none',
                                                  fontSize: '0.65rem',
                                                  padding: '2px 8px',
                                                  minWidth: 'auto'
                                                }}
                                              >
                                                <span className={styles.iconOnlyMobileLabel}>Duplicate</span>
                                              </Button>
                                            )}
                                          </Box>
                                          <PermissionsControl
                                            permissions={findPermissionByModule(
                                              14,
                                              permissions
                                            )}
                                            builderType={builderData.urlDesignType}
                                            readonlyForChangablePro
                                            inlineNotChangeableNotice
                                          >
                                            <FileCarousel
                                              files={builderData.images}
                                              themeColor={
                                                builderData.primaryColor.color
                                              }
                                              enablePreview
                                              builderType={builderType}
                                              slidesPerRow={2}
                                              isFree={isFree}
                                            />
                                          </PermissionsControl>
                                        </Grid>
                                      )}
                                      {section === "testimonials" && (
                                        <Grid
                                          item
                                          xs={12}
                                          className="section-wrapper"
                                          id="testimonials"
                                        >
                                          <Typography variant="h6" gutterBottom className={styles.sectionTitle}>
                                            Testimonials {isFree && <span className="pro_tag c-pointer">PRO</span>}
                                            <SectionInfoTooltip content={SECTION_TOOLTIPS.testimonials} />
                                            <ModuleSettings
                                              permissions={findPermissionByModule(
                                                17,
                                                permissions
                                              )}
                                              builderType={
                                                builderData.urlDesignType
                                              }
                                            />
                                          </Typography>
                                          <PermissionsControl
                                            permissions={findPermissionByModule(
                                              17,
                                              permissions
                                            )}
                                            builderType={builderData.urlDesignType}
                                            readonlyForChangablePro
                                            inlineNotChangeableNotice
                                          >
                                            <Testimonials
                                              builderType={builderType}
                                              isFree={isFree}
                                            />
                                          </PermissionsControl>
                                        </Grid>
                                      )}
                                      {section === "cta" && (
                                        <Grid
                                          item
                                          xs={12}
                                          className="section-wrapper"
                                          id="cta"
                                        >
                                          <Box position="relative">
                                            <Typography
                                              variant="h6"
                                              gutterBottom
                                              style={{ marginTop: ".5em" }}
                                              className={styles.sectionTitle}
                                            >
                                              Custom CTA Button {isFree && <span className="pro_tag c-pointer">PRO</span>}
                                              <SectionInfoTooltip content={SECTION_TOOLTIPS.cta} />
                                              <ModuleSettings
                                                permissions={findPermissionByModule(
                                                  23,
                                                  permissions
                                                )}
                                                builderType={
                                                  builderData.urlDesignType
                                                }
                                              />
                                            </Typography>
                                            {canChangeSection(23) && (
                                              <Box style={{ position: 'absolute', top: 0, right: 0, display: 'flex', gap: '4px' }}>
                                                <Button
                                                  size="small"
                                                  onClick={() => handleDuplicateSection('cta', '')}
                                                  variant="outlined"
                                                  startIcon={<FileCopyIcon style={{ fontSize: '14px' }} />}
                                                  className={styles.iconOnlyMobileButton}
                                                  style={{ 
                                                    textTransform: 'none', 
                                                    fontSize: '0.65rem',
                                                    padding: '2px 8px',
                                                    minWidth: 'auto'
                                                  }}
                                                >
                                                  <span className={styles.iconOnlyMobileLabel}>Duplicate</span>
                                                </Button>
                                              </Box>
                                            )}
                                          </Box>
                                          <PermissionsControl
                                            permissions={findPermissionByModule(
                                              23,
                                              permissions
                                            )}
                                            builderType={builderData.urlDesignType}
                                          >
                                            <CustomCTA builderType={builderType} isFree={isFree} />
                                          </PermissionsControl>
                                        </Grid>
                                      )}
                                      {section === "apps" && (
                                        <Grid
                                          item
                                          xs={12}
                                          className="section-wrapper"
                                          id="apps"
                                        >
                                          <Typography variant="h6" gutterBottom className={styles.sectionTitle}>
                                            App. Stores {isFree && <span className="pro_tag c-pointer">PRO</span>}
                                            <SectionInfoTooltip content={SECTION_TOOLTIPS.apps} />
                                            <ModuleSettings
                                              permissions={findPermissionByModule(
                                                24,
                                                permissions
                                              )}
                                              builderType={
                                                builderData.urlDesignType
                                              }
                                            />
                                          </Typography>
                                          <PermissionsControl
                                            permissions={findPermissionByModule(
                                              24,
                                              permissions
                                            )}
                                            builderType={builderData.urlDesignType}
                                          >
                                            <AppStores builderType={builderType} isFree={isFree} />
                                            <Box marginTop="1em">
                                              <AppStoreList
                                                appStores={builderData.appStores}
                                                onItemClick={(store) =>
                                                  window.open(store.storeLink)
                                                }
                                              />
                                            </Box>
                                          </PermissionsControl>
                                        </Grid>
                                      )}
                                      {section === "payment" && (
                                        <Grid
                                          item
                                          xs={12}
                                          className="section-wrapper"
                                          id="payment"
                                        >
                                          <Typography
                                            variant="h6"
                                            gutterBottom
                                            style={{ marginTop: ".5em" }}
                                            className={styles.sectionTitle}
                                          >
                                            Payment Options {isFree && <span className="pro_tag c-pointer">PRO</span>}
                                            <SectionInfoTooltip content={SECTION_TOOLTIPS.payment} />
                                            <ModuleSettings
                                              permissions={findPermissionByModule(
                                                25,
                                                permissions
                                              )}
                                              builderType={
                                                builderData.urlDesignType
                                              }
                                            />
                                          </Typography>
                                          <PermissionsControl
                                            permissions={findPermissionByModule(
                                              25,
                                              permissions
                                            )}
                                            builderType={builderData.urlDesignType}
                                          >
                                            <PaymentOptionList
                                              paymentOptions={
                                                builderData.paymentOptions
                                              }
                                              builderType={builderType}
                                              isFree={isFree}
                                            />
                                          </PermissionsControl>
                                        </Grid>
                                      )}
                                      {section === "reachout" && (
                                        <Grid
                                          item
                                          xs={12}
                                          className="section-wrapper"
                                          id="reachout"
                                        >
                                          <Typography variant="h6" gutterBottom className={styles.sectionTitle}>
                                            Reach Out {isFree && <span className="pro_tag c-pointer">PRO</span>}
                                            <SectionInfoTooltip content={SECTION_TOOLTIPS.reachout} />
                                            <ModuleSettings
                                              permissions={findPermissionByModule(
                                                26,
                                                permissions
                                              )}
                                              builderType={
                                                builderData.urlDesignType
                                              }
                                            />
                                          </Typography>
                                          <PermissionsControl
                                            permissions={findPermissionByModule(
                                              26,
                                              permissions
                                            )}
                                            builderType={builderData.urlDesignType}
                                          >
                                            <ContactMe builderType={builderType} isFree={isFree} />
                                          </PermissionsControl>
                                        </Grid>
                                      )}
                                    </div>
                                  )}
                                </Draggable>
                              );
                            });
                          })()}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                </Grid>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Container>
      {!isDragging && (
        <DesignerControlBar
          builderType={builderType}
          userData={userData}
          permissions={findPermissionByModule(
            BuilderPermissions.colourPicker,
            permissions
          )}
          permissionSetId={permissionSetId}
        />
      )}

      {/* Duplicate Section Confirmation Dialog */}
      <Dialog
        open={duplicateDialogOpen}
        onClose={() => {
          setDuplicateDialogOpen(false);
          setPendingDuplicateAction(null);
        }}
        aria-labelledby="duplicate-dialog-title"
        aria-describedby="duplicate-dialog-description"
      >
        <DialogTitle id="duplicate-dialog-title">Duplicate Section</DialogTitle>
        <DialogContent>
          <DialogContentText
            id="duplicate-dialog-description"
            style={{ color: "#2F2F2F" }}
          >
          Are you sure you want to duplicate this section? This action will create a new blank copy, and the duplicated section will inherit the same permissions as the original.

          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDuplicateDialogOpen(false);
              setPendingDuplicateAction(null);
            }}
            color="default"
            disabled={isDuplicating}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDuplicateSection}
            color="primary"
            variant="contained"
            disabled={isDuplicating}
          >
            {isDuplicating ? <CircularProgress size={18} color="inherit" /> : "Duplicate"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove Duplicate Section Confirmation Dialog */}
      <Dialog
        open={removeDialogOpen}
        onClose={() => {
          setRemoveDialogOpen(false);
          setPendingRemoveAction(null);
        }}
        aria-labelledby="remove-dialog-title"
        aria-describedby="remove-dialog-description"
      >
        <DialogTitle id="remove-dialog-title">Remove Section</DialogTitle>
        <DialogContent>
          <DialogContentText
            id="remove-dialog-description"
            style={{ color: "#2F2F2F" }}
          >
            Are you sure you want to remove this duplicate section? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setRemoveDialogOpen(false);
              setPendingRemoveAction(null);
            }}
            color="default"
            disabled={isRemovingDuplicate}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmRemoveDuplicateSection}
            color="secondary"
            variant="contained"
            disabled={isRemovingDuplicate}
          >
            {isRemovingDuplicate ? <CircularProgress size={18} color="inherit" /> : "Remove"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
